import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Map, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/cities`;

const EMPTY_FORM = { country_id: "", state_id: "", name: "", description: "" };

export default function CitiesPage() {
  const { view, setView, list: cities, inactiveCount, loading, error, reload } = useActiveInactive(BASE);
  const [countries, setCountries] = useState([]);
  const [states, setStates]       = useState([]);

  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formErr, setFormErr]     = useState("");
  const [saving, setSaving]       = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const statesForSelectedCountry = useMemo(
    () => states.filter((s) => s.country_id === form.country_id),
    [states, form.country_id]
  );

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "country_id") next.state_id = "";
      return next;
    });
  };

  const fetchAux = useCallback(async () => {
    try {
      const [sRes, ctRes] = await Promise.all([
        axios.get(`${API}/api/states`,    ax()),
        axios.get(`${API}/api/countries`, ax()),
      ]);
      setStates(sRes.data);
      setCountries(ctRes.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", city: null }); };
  const openEdit = (c) => {
    setForm({ country_id: c.country_id, state_id: c.state_id, name: c.name, description: c.description || "" });
    setFormErr(""); setModal({ mode: "edit", city: c });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.country_id)  { setFormErr("Please select a country."); return; }
    if (!form.state_id)    { setFormErr("Please select a state.");   return; }
    if (!form.name.trim()) { setFormErr("Name is required."); return; }
    setSaving(true); setFormErr("");
    try {
      if (modal.mode === "add") await axios.post(BASE, form, ax());
      else                       await axios.put(`${BASE}/${modal.city.id}`, form, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch (e) { alert(formatApiError(e, "Could not delete.")); setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  const needsParents = countries.length === 0 || states.length === 0;

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Cities</h1>
          <p className="page-sub">Manage cities under each state.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} disabled={needsParents} data-testid="add-city-btn">
            <Plus size={16} /> Add City
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="cities-view" />

      {needsParents && !loading && view === "active" && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          You need at least one country and one state before adding cities.
        </div>
      )}

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {cities.length === 0 ? (
            <div className="empty-state">
              <Map size={32} color="#43E0FF" />
              {view === "active"
                ? <p>No cities yet. Click <strong>Add City</strong> to create one.</p>
                : <p>No inactive cities.</p>}
            </div>
          ) : (
            <table className="data-table" data-testid="cities-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Country</th>
                  <th>State</th>
                  <th>City Name</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cities.map((c, i) => (
                  <tr key={c.id} data-testid={`city-row-${c.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-desc">{c.country_name}</td>
                    <td className="td-desc">
                      <span className="subject-pill">
                        {c.state_code && <span className="subject-pill-code">{c.state_code}</span>}
                        {c.state_name}
                      </span>
                    </td>
                    <td className="td-name">{c.name}</td>
                    <td className="td-desc">{c.description || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(c)}        data-testid={`edit-city-${c.id}`}   title="Edit"><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(c.id)} data-testid={`delete-city-${c.id}`} title="Delete"><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(c.id)} disabled={restoringId === c.id} data-testid={`restore-city-${c.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="city-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add City" : "Edit City"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Country <span className="required">*</span></label>
                  <select className="field-input" value={form.country_id} onChange={onChange("country_id")} autoFocus data-testid="city-country-select">
                    <option value="">Select a country…</option>
                    {countries.map((c) => (<option key={c.id} value={c.id}>{c.country_code} — {c.name}</option>))}
                  </select>
                </div>
                <div className="field">
                  <label className="field-label">State <span className="required">*</span></label>
                  <select className="field-input" value={form.state_id} onChange={onChange("state_id")}
                    disabled={!form.country_id} data-testid="city-state-select">
                    <option value="">{form.country_id ? "Select a state…" : "Select a country first"}</option>
                    {statesForSelectedCountry.map((s) => (<option key={s.id} value={s.id}>{s.state_code} — {s.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="field">
                <label className="field-label">City Name <span className="required">*</span></label>
                <input className="field-input" placeholder="Bengaluru" value={form.name}
                  onChange={onChange("name")} maxLength={120} data-testid="city-name-input" />
              </div>
              <div className="field">
                <label className="field-label">Description</label>
                <textarea className="field-input field-textarea" placeholder="Optional notes"
                  value={form.description} onChange={onChange("description")} rows={3} maxLength={600}
                  data-testid="city-description-input" />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-city-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add City" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete City</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this city to <strong>Inactive</strong>? It will be hidden from selection lists but can be restored later.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
